import { Button, Checkbox, Slider, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import BrightnessIcon from '@material-ui/icons/Brightness6';
import CloseIcon from '@material-ui/icons/Close';
import CropIcon from '@material-ui/icons/Crop';
import PhotoIcon from '@material-ui/icons/Photo';
import SaveIcon from '@material-ui/icons/Save';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { AppContext } from '../../../AppContext';
import { updateFormImage, createFormImage } from '../../../services';
import { OrderReviewContext } from '../../QaOrderReview/OrderReviewContext';
import {
  cancelImagePreview,
  saveImageSuccess,
  updateBrightness,
  updateCropping,
  updateImageCrop,
  updatePreviewing,
  updateSaving,
  viewImagePreview,
  saveImage,
} from '../actions';
import { ImageEditorContext } from '../context';
import styles from './Toolbar.styles';
import { UPDATE_KEEP_ASPECT_RATIO } from '../constants';

const propTypes = {
  classes: PropTypes.object,
  applyFilters: PropTypes.func.isRequired,
  cropToolRef: PropTypes.any,
  imageRef: PropTypes.any,
  formImage: PropTypes.any,
  image: PropTypes.object.isRequired,
};

function Toolbar({ classes, applyFilters, imageRef, cropToolRef, image, formImage }) {
  const {
    state: { cropping, previewing, saving, wasCropping, brightness, cropScale, keepAspectRatio },
    dispatch,
  } = useContext(ImageEditorContext);
  const {
    dispatch: orderReviewDispatch,
    state: { isFieldRep },
  } = useContext(OrderReviewContext);
  const { dispatch: appDispatch } = useContext(AppContext);

  const onChangeBrightness = (event, value) => {
    dispatch(updateBrightness(value));
    applyFilters();
  };

  const onClickCancel = () => {
    dispatch(cancelImagePreview(wasCropping));
  };

  const onClickPreview = () => {
    dispatch(viewImagePreview());
    imageRef.current.clearCache();
    if (cropToolRef.current) {
      const cropTool = cropToolRef.current.getAttrs();
      const scaledX = cropTool.x * cropScale.x;
      const scaledY = cropTool.y * cropScale.y;
      const cropToolWidth = cropTool.width * cropScale.x * cropTool.scaleX;
      const cropToolHeight = cropTool.height * cropScale.y * cropTool.scaleY;
      const cropToolX = formImage.rotateDegree === 180 ? image.width - scaledX - cropToolWidth : scaledX;
      const cropToolY = formImage.rotateDegree === 180 ? image.height - scaledY - cropToolHeight : scaledY;
      const newImageCrop = {
        x: cropToolX,
        y: cropToolY,
        width: cropToolWidth,
        height: cropToolHeight,
      };

      dispatch(updateImageCrop(newImageCrop));
    } else {
      applyFilters();
    }
    dispatch(updatePreviewing(true));
  };

  const onClickSave = async () => {
    try {
      dispatch(saveImage());
      const base64 = imageRef.current.toDataURL();
      const newFormImage = await updateFormImage(formImage.formQaImageId, base64);
      const updatedFormImage = Object.assign({}, formImage);
      // Append new Date().getTime() to force image cache refresh by browser
      updatedFormImage.imageUri = `${newFormImage.imageUri}#${new Date().getTime()}`;
      updatedFormImage.rotateDegree = 0;
      orderReviewDispatch({
        type: 'UPDATE_IMAGE',
        image: updatedFormImage,
      });
      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: 'Changes saved.',
          variant: 'success',
        },
      });
      dispatch(saveImageSuccess());
    } catch (response) {
      dispatch(updateSaving(false));
      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: `${response.errorMessage ? response.errorMessage : response}`,
          variant: 'error',
        },
      });
    }
  };
  const onClickSaveAsNewImage = async () => {
    try {
      dispatch(saveImage());
      const base64 = imageRef.current.toDataURL();
      const newFormImage = await createFormImage(formImage.formQaImageId, base64);
      // Mapping formImageId to formQaImageId to support legacy endpoints.
      // At some point, we should remove all instances of formQaImageId in frontend
      // and backend and use formImageId exclusively.
      newFormImage.formQaImageId = newFormImage.formImageId;
      orderReviewDispatch({
        type: 'ADD_IMAGE',
        image: newFormImage,
      });
      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: 'New photo created.',
          variant: 'success',
        },
      });
      dispatch(saveImageSuccess());
    } catch (response) {
      dispatch(updateSaving(false));
      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: `${response.errorMessage ? response.errorMessage : response}`,
          variant: 'error',
        },
      });
    }
  };

  return (
    <div className={classes.toolbar}>
      <div className={classes.leftToolbar}>
        {previewing ? (
          <>
            <Button
              aria-label="save as new image"
              className={clsx(classes.button, classes.saveAsNewButton)}
              onClick={onClickSaveAsNewImage}
              disabled={saving || !previewing}
            >
              Save as New Image <AddPhotoAlternateIcon className={classes.rightIcon} />
            </Button>

            {!isFieldRep && (
              <Button className={clsx(classes.button, classes.saveButton)} onClick={onClickSave}>
                Save Changes <SaveIcon className={classes.rightIcon} />
              </Button>
            )}
          </>
        ) : (
          <React.Fragment>
            <Button
              aria-label="crop image"
              className={clsx(classes.button, classes.cropButton, cropping && classes.cropping)}
              onClick={() => dispatch(updateCropping(!cropping))}
              disabled={saving || previewing}
            >
              {cropping && 'Exit'} Crop <CropIcon className={classes.rightIcon} />
            </Button>
            <div className={classes.brightnessSlider}>
              <BrightnessIcon
                className={clsx(classes.brightnessIcon, (saving || previewing) && classes.brightnessIconDisabled)}
              />
              <Slider
                value={brightness}
                valueLabelDisplay="off"
                onChange={onChangeBrightness}
                step={0.01}
                min={-0.5}
                max={0.5}
                disabled={saving || previewing}
              />
            </div>

            <div
              className="row-center-center mx-10"
              style={{
                opacity: cropping ? 1 : 0.4,
              }}
            >
              <Checkbox
                //
                id="edit-photo-aspect-ratio"
                checked={keepAspectRatio}
                style={{
                  color: 'var(--jmi-blue)',
                }}
                onChange={e => {
                  dispatch({
                    type: UPDATE_KEEP_ASPECT_RATIO,
                    value: e.target.checked,
                  });
                }}
                disabled={!cropping || saving || previewing}
              />
              <Typography component={'label'} for="edit-photo-aspect-ratio">
                Keep Aspect Ratio
              </Typography>
            </div>
          </React.Fragment>
        )}
      </div>
      <div className={classes.rightToolbar}>
        {!previewing && (
          <Button className={classes.button} onClick={onClickPreview} disabled={saving}>
            Preview <PhotoIcon className={classes.rightIcon} />
          </Button>
        )}
        {previewing && !saving && (
          <React.Fragment>
            <Button className={clsx(classes.button, classes.cancelButton)} onClick={onClickCancel}>
              Cancel <CloseIcon className={classes.rightIcon} />
            </Button>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

Toolbar.propTypes = propTypes;
export default withStyles(styles)(Toolbar);
